"use client";

/**
 * SPATIAL OS AUDIO LAYER
 *
 * Philosophy: diegetic minimalism. Sounds belong to the world.
 * Clean precise system (Stellar Blade register) + vast environment
 * (Voyager/server room) + imperfect transmission (CRT interference).
 *
 * Library: Tone.js
 */

import * as Tone from "tone";

// =============================================================================
// STATE
// =============================================================================

// Track initialization separately from context state.
// Tone.context.state === "running" breaks when muted (context is suspended),
// causing setMute(false) to bail early and never resume.
let _initialized = false;
function isInitialized(): boolean {
  return _initialized;
}

// Load mute state immediately on module load (survives HMR)
function getInitialMuteState(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem("vn-mute") === "true";
}
let muted = getInitialMuteState();

// Check mute state - always reads localStorage as source of truth
function isMuted(): boolean {
  if (typeof window === "undefined") return true;
  // Sync module variable from localStorage
  const stored = localStorage.getItem("vn-mute") === "true";
  if (stored !== muted) {
    muted = stored;
  }
  return muted;
}
let gpuTier = 3; // Default to full audio, will be set by setGpuTier()

// Persistent nodes (created once on init)
let ambientOsc1: Tone.Oscillator | null = null;
let ambientOsc2: Tone.Oscillator | null = null;
let ambientReverb: Tone.Reverb | null = null;
let ambientFilter: Tone.Filter | null = null;
let ambientVolume: Tone.Volume | null = null;

let rotationOsc: Tone.Oscillator | null = null;
let rotationFilter: Tone.AutoFilter | null = null;
let rotationVolume: Tone.Volume | null = null;

// Boot sequence nodes
let bootOsc: Tone.Oscillator | null = null;
let bootVolume: Tone.Volume | null = null;
let bootFilter: Tone.Filter | null = null;

// Hover debounce
let lastHoverTime = 0;
const HOVER_DEBOUNCE_MS = 200;

// UI hover debounce (separate from orb hover)
let lastUIHoverTime = 0;
const UI_HOVER_DEBOUNCE_MS = 100;

// =============================================================================
// HOVER PITCH MAP (pentatonic scale)
// =============================================================================

const HOVER_PITCHES: Record<number, number> = {
  0: 293,  // D5  - profile orb, lowest/most grounded
  1: 370,  // F#5
  2: 440,  // A5
  3: 494,  // B5
  4: 587,  // D6
  5: 740,  // F#6
  6: 880,  // A6
  7: 988,  // B6
};

// =============================================================================
// MUTE SYSTEM
// =============================================================================

const MUTE_KEY = "vn-mute";

function loadMuteState(): boolean {
  if (typeof window === "undefined") return false;
  const stored = localStorage.getItem(MUTE_KEY);
  return stored === "true";
}

function saveMuteState(value: boolean): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(MUTE_KEY, value.toString());
}

export function setMute(value: boolean): void {
  muted = value;
  saveMuteState(value);

  if (!isInitialized()) return;

  const dest = Tone.getDestination();
  // Use both mute property AND volume for maximum reliability across browsers
  dest.mute = muted;
  dest.volume.value = muted ? -100 : 0;

  // Suspend/resume the entire audio context - this definitively stops all sound
  const ctx = Tone.context.rawContext as AudioContext;

  if (muted) {
    // Stop blips (scheduled timeouts) but leave oscillator nodes alive.
    // Calling .stop() on Tone.js oscillators is one-shot — they can't be
    // restarted. Suspending the AudioContext silences everything without
    // destroying the nodes, so unmute can resume cleanly.
    stopAmbientBlips();
    if (ctx.state === "running") {
      ctx.suspend();
      console.log("[SpatialAudio] Context suspended (muted)");
    }
  } else {
    // Resume context — oscillator nodes are still alive from init, they
    // just need the context running again. Then restart blip scheduling.
    if (ctx.state === "suspended") {
      ctx.resume().then(() => {
        console.log("[SpatialAudio] Context resumed (unmuted)");
        // startAmbient is idempotent — no-op if oscillators already running,
        // but required if user loaded the page with mute on (oscillators were
        // never .start()ed because canPlayAmbient() blocked them during init).
        startAmbient();
        startAmbientBlips();
        playAudioOnConfirm();
      });
    } else {
      startAmbient();
      startAmbientBlips();
      playAudioOnConfirm();
    }
  }
}

/**
 * Gentle confirmation tone when audio is turned on.
 * Very soft, short sine tone at A4 (440hz).
 */
function playAudioOnConfirm(): void {
  if (gpuTier < 1 || !isInitialized()) return;

  const synth = new Tone.Synth({
    oscillator: { type: "sine" },
    envelope: { attack: 0.01, decay: 0.2, sustain: 0, release: 0.1 },
  });
  const vol = new Tone.Volume(-32); // Very soft

  synth.connect(vol);
  vol.toDestination();

  synth.triggerAttackRelease(440, 0.15);

  setTimeout(() => {
    synth.dispose();
    vol.dispose();
  }, 400);
}

export function getMute(): boolean {
  return muted;
}

// =============================================================================
// GPU TIER GATING
// =============================================================================

/**
 * T0: full silence, Tone never initialized
 * T1: ambient only
 * T2: ambient + boot settle/flash + select/snap (no hover, no glitch)
 * T3: full system
 */
export function setGpuTier(tier: number): void {
  gpuTier = tier;
}

function canPlayAmbient(): boolean {
  return gpuTier >= 1 && !isMuted() && isInitialized();
}

function canPlayBoot(): boolean {
  return gpuTier >= 2 && !isMuted() && isInitialized();
}

function canPlayInteraction(): boolean {
  return gpuTier >= 2 && !isMuted() && isInitialized();
}

function canPlayHover(): boolean {
  return gpuTier >= 3 && !isMuted() && isInitialized();
}

function canPlayGlitch(): boolean {
  return gpuTier >= 3 && !isMuted() && isInitialized();
}

function canPlayTransition(): boolean {
  return gpuTier >= 3 && !isMuted() && isInitialized();
}

// =============================================================================
// INITIALIZATION
// =============================================================================

/**
 * Call on first user gesture (click/tap/keydown).
 * Must be called before any other audio functions will work.
 */
export async function initAudio(): Promise<void> {
  if (isInitialized()) return;
  if (gpuTier < 1) return; // T0: no audio

  try {
    await Tone.start();
    console.log("[SpatialAudio] Tone.js context started");

    // Load mute state and apply to destination
    muted = loadMuteState();
    if (muted) {
      const dest = Tone.getDestination();
      dest.mute = true;
      dest.volume.value = -100;
      // Suspend context immediately if muted - MUST await!
      const ctx = Tone.context.rawContext as AudioContext;
      if (ctx.state === "running") {
        await ctx.suspend();
        console.log("[SpatialAudio] Context suspended on init (muted)");
      }
      // Still create nodes so unmuting works, but they won't play while suspended
    }

    // Create persistent ambient nodes
    await createAmbientNodes();

    // Create rotation nodes
    createRotationNodes();

    // Suspend/resume AudioContext when the tab or window loses visibility.
    // Three event layers for cross-browser coverage.
    // IMPORTANT: Only resume if NOT muted - respect user's mute preference.
    const ctx = Tone.context.rawContext as AudioContext;
    const suspendCtx = () => {
      // Stop blip timeouts so they don't queue up while backgrounded
      stopAmbientBlips();
      if (ctx.state === "running") ctx.suspend();
    };
    const resumeCtx = () => {
      // Never resume if user has muted audio
      if (isMuted()) return;
      if (ctx.state === "suspended") {
        ctx.resume().then(() => startAmbientBlips());
      } else {
        startAmbientBlips();
      }
    };

    document.addEventListener("visibilitychange", () => {
      document.hidden ? suspendCtx() : resumeCtx();
    });
    document.addEventListener("pagehide", suspendCtx);
    document.addEventListener("pageshow", resumeCtx);
    window.addEventListener("blur", suspendCtx);
    window.addEventListener("focus", resumeCtx);

    _initialized = true;
    console.log("[SpatialAudio] Initialized, gpuTier:", gpuTier, "muted:", muted);
  } catch (err) {
    console.error("[SpatialAudio] Failed to initialize:", err);
  }
}

async function createAmbientNodes(): Promise<void> {
  // Two detuned sine waves for natural beating
  // osc1: 41hz, osc2: 43.5hz = 2.5hz beat frequency

  ambientReverb = new Tone.Reverb({ decay: 10, wet: 0.6 });
  await ambientReverb.generate();

  ambientFilter = new Tone.Filter({ type: "highpass", frequency: 35 });
  ambientVolume = new Tone.Volume(-52); // Start very quiet

  ambientOsc1 = new Tone.Oscillator({ type: "sine", frequency: 41 });
  ambientOsc2 = new Tone.Oscillator({ type: "sine", frequency: 43.5 });

  // Chain: oscs -> filter -> reverb -> volume -> destination
  ambientOsc1.connect(ambientFilter);
  ambientOsc2.connect(ambientFilter);
  ambientFilter.connect(ambientReverb);
  ambientReverb.connect(ambientVolume);
  ambientVolume.toDestination();
}

function createRotationNodes(): void {
  rotationVolume = new Tone.Volume(-40);
  rotationFilter = new Tone.AutoFilter({ frequency: 2, wet: 0 }).start();
  rotationOsc = new Tone.Oscillator({ type: "sine", frequency: 180 });

  rotationOsc.connect(rotationFilter);
  rotationFilter.connect(rotationVolume);
  rotationVolume.toDestination();
}

// =============================================================================
// AMBIENT
// =============================================================================

export function startAmbient(): void {
  if (!canPlayAmbient()) return;
  if (!ambientOsc1 || !ambientOsc2) return;

  if (ambientOsc1.state !== "started") {
    ambientOsc1.start();
    ambientOsc2?.start();
    console.log("[SpatialAudio] Ambient started");
  }
}

export function stopAmbient(): void {
  if (!ambientOsc1 || !ambientOsc2) return;

  if (ambientOsc1.state === "started") {
    ambientOsc1.stop();
    ambientOsc2?.stop();
    console.log("[SpatialAudio] Ambient stopped");
  }
}

/**
 * Fade ambient volume based on ring progress.
 * -52db at start, -52db at ringProgress=1 (flat — drones sit as a bed
 * ~10db below ambient event tier so they never compete with interactions).
 */
export function setAmbientLevel(ringProgress: number): void {
  if (!ambientVolume) return;
  // Flat -52db (peak rebalanced from -42 → -52)
  const targetDb = -52 + (ringProgress * 0);
  ambientVolume.volume.value = targetDb;
}

/**
 * Reading state: drones fade to silence (-80db). Below audible threshold
 * so project reading stays undistracted. Ambient blips are already paused
 * in reading mode via setBlipsReadingMode.
 */
export function setAmbientReadingMode(reading: boolean): void {
  if (!ambientVolume) return;
  ambientVolume.volume.rampTo(reading ? -80 : -42, 1);
}

// =============================================================================
// BOOT SEQUENCE
// =============================================================================

export function playBoot(phase: "shrink" | "flash" | "void-tick" | "emergence" | "settle"): void {
  if (!canPlayBoot()) return;

  switch (phase) {
    case "shrink":
      playBootShrink();
      break;
    case "flash":
      playBootFlash();
      break;
    case "void-tick":
      playVoidTick();
      break;
    case "emergence":
      // Emergence is continuous, handled by setEmergenceProgress
      break;
    case "settle":
      playRingSettle();
      break;
  }
}

function playBootShrink(): void {
  // Sub-bass sine, 55hz, volume ramps from -40db to -28db over 2.4s
  bootFilter = new Tone.Filter({ type: "lowpass", frequency: 200 });
  bootVolume = new Tone.Volume(-40);
  bootOsc = new Tone.Oscillator({ type: "sine", frequency: 55 });

  bootOsc.connect(bootFilter);
  bootFilter.connect(bootVolume);
  bootVolume.toDestination();

  bootOsc.start();
  bootVolume.volume.rampTo(-28, 2.4);

  console.log("[SpatialAudio] Boot shrink started");
}

export function stopBootShrink(): void {
  if (bootOsc) {
    bootOsc.stop();
    bootOsc.dispose();
    bootOsc = null;
  }
  if (bootFilter) {
    bootFilter.dispose();
    bootFilter = null;
  }
  if (bootVolume) {
    bootVolume.dispose();
    bootVolume = null;
  }
}

function playBootFlash(): void {
  // Stop shrink sound
  stopBootShrink();

  // LAYER 1: Frequency sweep 2400hz -> 80hz, 80ms
  const sweepEnv = new Tone.FrequencyEnvelope({
    attack: 0,
    decay: 0.08,
    sustain: 0,
    release: 0,
    baseFrequency: 80,
    octaves: 5,
  });
  const sweepOsc = new Tone.Oscillator({ type: "sine" });
  sweepEnv.connect(sweepOsc.frequency);

  // LAYER 2: White noise burst, 60ms decay
  const noise = new Tone.Noise({ type: "white" });
  const noiseEnv = new Tone.AmplitudeEnvelope({
    attack: 0,
    decay: 0.06,
    sustain: 0,
    release: 0,
  });
  const noiseVol = new Tone.Volume(-28);

  // LAYER 3: Reverb tail
  const reverb = new Tone.Reverb({ decay: 1.2, wet: 0.5 });

  // Connect sweep
  const sweepVol = new Tone.Volume(-22);
  sweepOsc.connect(sweepVol);
  sweepVol.connect(reverb);

  // Connect noise
  noise.connect(noiseEnv);
  noiseEnv.connect(noiseVol);
  noiseVol.connect(reverb);

  reverb.toDestination();

  // Trigger
  sweepOsc.start();
  sweepEnv.triggerAttackRelease(0.08);
  noise.start();
  noiseEnv.triggerAttackRelease(0.06);

  // Cleanup after reverb tail
  setTimeout(() => {
    sweepOsc.stop();
    sweepOsc.dispose();
    sweepEnv.dispose();
    noise.stop();
    noise.dispose();
    noiseEnv.dispose();
    noiseVol.dispose();
    sweepVol.dispose();
    reverb.dispose();
  }, 1500);

  console.log("[SpatialAudio] Boot flash");
}

function playVoidTick(): void {
  // Single tick: sine, 800hz, 20ms, -52db
  const osc = new Tone.Oscillator({ type: "sine", frequency: 800 });
  const env = new Tone.AmplitudeEnvelope({
    attack: 0,
    decay: 0.02,
    sustain: 0,
    release: 0,
  });
  const vol = new Tone.Volume(-52);

  osc.connect(env);
  env.connect(vol);
  vol.toDestination();

  osc.start();
  env.triggerAttackRelease(0.02);

  setTimeout(() => {
    osc.stop();
    osc.dispose();
    env.dispose();
    vol.dispose();
  }, 100);
}

/**
 * Set emergence progress (0-1). FMSynth harmonicity and frequency ramp.
 */
let emergenceSynth: Tone.FMSynth | null = null;
let emergenceVolume: Tone.Volume | null = null;
let emergenceFilter: Tone.Filter | null = null;

export function startEmergence(): void {
  if (!canPlayBoot()) return;
  if (emergenceSynth) return;

  emergenceFilter = new Tone.Filter({ type: "lowpass", frequency: 800 });
  emergenceVolume = new Tone.Volume(-38);
  emergenceSynth = new Tone.FMSynth({
    harmonicity: 0,
    modulationIndex: 2,
    oscillator: { type: "sine" },
    modulation: { type: "sine" },
    envelope: { attack: 0.1, decay: 0, sustain: 1, release: 0.5 },
    modulationEnvelope: { attack: 0.1, decay: 0, sustain: 1, release: 0.5 },
  });

  emergenceSynth.connect(emergenceFilter);
  emergenceFilter.connect(emergenceVolume);
  emergenceVolume.toDestination();

  emergenceSynth.triggerAttack(120);
  console.log("[SpatialAudio] Emergence started");
}

export function setEmergenceProgress(progress: number): void {
  if (!emergenceSynth || !emergenceVolume) return;

  // Harmonicity: 0 -> 4
  emergenceSynth.harmonicity.value = progress * 4;
  // Frequency: 120 -> 180
  emergenceSynth.frequency.value = 120 + (progress * 60);
  // Volume: -38 -> -22
  emergenceVolume.volume.value = -38 + (progress * 16);
}

export function stopEmergence(): void {
  if (emergenceSynth) {
    emergenceSynth.triggerRelease();
    setTimeout(() => {
      emergenceSynth?.dispose();
      emergenceSynth = null;
      emergenceFilter?.dispose();
      emergenceFilter = null;
      emergenceVolume?.dispose();
      emergenceVolume = null;
    }, 600);
  }
}

function playRingSettle(): void {
  // ONE note. The whole boot earns this.
  // MetalSynth, 432hz, sharp attack, long decay

  stopEmergence();

  const reverb = new Tone.Reverb({ decay: 3, wet: 0.4 });
  const eq = new Tone.EQ3({ high: 3 }); // +3db at 2khz
  const vol = new Tone.Volume(-18);

  const synth = new Tone.MetalSynth({
    envelope: {
      attack: 0.002,
      decay: 1.8,
      release: 0,
    },
    harmonicity: 5.1,
    modulationIndex: 16,
    resonance: 4000,
    octaves: 1.5,
  });

  synth.connect(eq);
  eq.connect(reverb);
  reverb.connect(vol);
  vol.toDestination();

  // 432hz = approximately G4
  synth.triggerAttackRelease(432, 1.8);

  // Cleanup after decay
  setTimeout(() => {
    synth.dispose();
    eq.dispose();
    reverb.dispose();
    vol.dispose();
  }, 5000);

  console.log("[SpatialAudio] Ring settle");
}

// =============================================================================
// RING INTERACTIONS
// =============================================================================

export function playHover(orbIndex: number): void {
  if (!canPlayHover()) return;

  // Debounce
  const now = Date.now();
  if (now - lastHoverTime < HOVER_DEBOUNCE_MS) return;
  lastHoverTime = now;

  const freq = HOVER_PITCHES[orbIndex] ?? 440;

  // Muffled bubble — sub-octave sine with heavy lowpass, slow fade
  const subFreq = freq / 2;
  const filter = new Tone.Filter({ type: "lowpass", frequency: 400, Q: 0.5 });
  const vol = new Tone.Volume(-24);
  const synth = new Tone.Synth({
    oscillator: { type: "sine" },
    envelope: { attack: 0.02, decay: 0.25, sustain: 0, release: 0.15 },
  });

  synth.connect(filter);
  filter.connect(vol);
  vol.toDestination();

  synth.triggerAttackRelease(subFreq, 0.25);

  setTimeout(() => {
    synth.dispose();
    filter.dispose();
    vol.dispose();
  }, 500);
}

export function playSelect(orbIndex: number): void {
  if (!canPlayInteraction()) return;

  const freq = HOVER_PITCHES[orbIndex] ?? 440;

  // Short reverb for spatial tail
  const reverb = new Tone.Reverb({ decay: 1, wet: 0.3 });

  // LAYER 1: Triangle wave main note — richer harmonics than sine
  const synth1 = new Tone.Synth({
    oscillator: { type: "triangle" },
    envelope: { attack: 0.002, decay: 0.35, sustain: 0, release: 0 },
  });
  const vol1 = new Tone.Volume(-16);
  synth1.connect(vol1);
  vol1.connect(reverb);

  // LAYER 2: Noise click transient — percussive attack
  const noise = new Tone.Noise({ type: "white" });
  const noiseFilter = new Tone.Filter({ type: "bandpass", frequency: 2000, Q: 4 });
  const noiseEnv = new Tone.AmplitudeEnvelope({
    attack: 0,
    decay: 0.015,
    sustain: 0,
    release: 0,
  });
  const noiseVol = new Tone.Volume(-30);
  noise.connect(noiseFilter);
  noiseFilter.connect(noiseEnv);
  noiseEnv.connect(noiseVol);
  noiseVol.toDestination();

  // LAYER 3: Octave up shimmer
  const synth2 = new Tone.Oscillator({ type: "sine", frequency: freq * 2 });
  const env2 = new Tone.AmplitudeEnvelope({
    attack: 0.002,
    decay: 0.2,
    sustain: 0,
    release: 0,
  });
  const vol2 = new Tone.Volume(-28);
  synth2.connect(env2);
  env2.connect(vol2);
  vol2.connect(reverb);

  reverb.toDestination();

  // Trigger all layers
  synth1.triggerAttackRelease(freq, 0.35);
  noise.start();
  noiseEnv.triggerAttackRelease(0.015);
  synth2.start();
  env2.triggerAttackRelease(0.2);

  setTimeout(() => {
    synth1.dispose();
    vol1.dispose();
    noise.stop();
    noise.dispose();
    noiseFilter.dispose();
    noiseEnv.dispose();
    noiseVol.dispose();
    synth2.stop();
    synth2.dispose();
    env2.dispose();
    vol2.dispose();
    reverb.dispose();
  }, 1200);
}

export function playRotation(velocity: number): void {
  if (!canPlayInteraction()) return;
  if (!rotationOsc || !rotationVolume || !rotationFilter) return;

  if (velocity < 0.05) {
    // Stop rotation sound
    if (rotationOsc.state === "started") {
      rotationOsc.stop();
    }
    return;
  }

  // Start if not playing
  if (rotationOsc.state !== "started") {
    rotationOsc.start();
  }

  // Clamp velocity for audio calculations (ring can spin fast)
  const clampedVel = Math.min(1, velocity);

  // Frequency: 180hz + (velocity * 40hz)
  rotationOsc.frequency.value = 180 + (clampedVel * 40);
  // Volume: -40db + (velocity * 12db)
  rotationVolume.volume.value = -40 + (clampedVel * 12);
  // AutoFilter wet: velocity * 0.3 (must be 0-1)
  rotationFilter.wet.value = clampedVel * 0.3;
}

export function playSnap(): void {
  if (!canPlayInteraction()) return;

  // Stop rotation sound immediately
  if (rotationOsc?.state === "started") {
    rotationOsc.stop();
  }

  // Muted thud — low sine pulse, heavy lowpass, no high-end
  const filter = new Tone.Filter({ type: "lowpass", frequency: 300, Q: 0.3 });
  const vol = new Tone.Volume(-20);
  const synth = new Tone.Synth({
    oscillator: { type: "sine" },
    envelope: { attack: 0.008, decay: 0.12, sustain: 0, release: 0.08 },
  });

  synth.connect(filter);
  filter.connect(vol);
  vol.toDestination();

  synth.triggerAttackRelease(180, 0.12);

  setTimeout(() => {
    synth.dispose();
    filter.dispose();
    vol.dispose();
  }, 300);
}

/**
 * Generic UI hover sound for links, images, buttons in case study content.
 * Subtle soft click - much quieter and shorter than orb hover.
 * T3+: plays. T0-T2: no-op.
 *
 * @param orbIndex - Optional project orb index. When provided, uses the project's
 *                   pentatonic pitch (same as orb hover). When omitted, uses
 *                   neutral 600hz for UI elements like devtools, corner navs, close buttons.
 */
export function playUIHover(orbIndex?: number): void {
  if (!canPlayHover()) return;

  // Debounce
  const now = Date.now();
  if (now - lastUIHoverTime < UI_HOVER_DEBOUNCE_MS) return;
  lastUIHoverTime = now;

  // Use project pitch if orbIndex provided, otherwise neutral 600hz
  const freq = orbIndex !== undefined ? (HOVER_PITCHES[orbIndex] ?? 600) : 600;

  // LAYER 1: Triangle wave tick — richer than pure sine
  const vol1 = new Tone.Volume(-32);
  const synth = new Tone.Synth({
    oscillator: { type: "triangle" },
    envelope: { attack: 0.002, decay: 0.035, sustain: 0, release: 0 },
  });
  synth.connect(vol1);
  vol1.toDestination();

  // LAYER 2: Micro pink noise for texture
  const noise = new Tone.Noise({ type: "pink" });
  const noiseFilter = new Tone.Filter({ type: "highpass", frequency: 4000 });
  const noiseEnv = new Tone.AmplitudeEnvelope({
    attack: 0,
    decay: 0.008,
    sustain: 0,
    release: 0,
  });
  const noiseVol = new Tone.Volume(-44);
  noise.connect(noiseFilter);
  noiseFilter.connect(noiseEnv);
  noiseEnv.connect(noiseVol);
  noiseVol.toDestination();

  synth.triggerAttackRelease(freq, 0.035);
  noise.start();
  noiseEnv.triggerAttackRelease(0.008);

  setTimeout(() => {
    synth.dispose();
    vol1.dispose();
    noise.stop();
    noise.dispose();
    noiseFilter.dispose();
    noiseEnv.dispose();
    noiseVol.dispose();
  }, 100);
}

/**
 * Touch tap sound — punchier than hover, used on mobile where there's no hover.
 * Short FM burst + noise transient. No reverb (tight on mobile).
 * T2+: full sound. T0-T1: no-op.
 */
export function playTap(orbIndex: number): void {
  if (!canPlayInteraction()) return;

  const freq = HOVER_PITCHES[orbIndex] ?? 440;

  // LAYER 1: Triangle wave, punchy 80ms
  const vol1 = new Tone.Volume(-22);
  const synth = new Tone.Synth({
    oscillator: { type: "triangle" },
    envelope: { attack: 0.001, decay: 0.08, sustain: 0, release: 0 },
  });
  synth.connect(vol1);
  vol1.toDestination();

  // LAYER 2: Noise transient for tactile feel
  const noise = new Tone.Noise({ type: "white" });
  const noiseFilter = new Tone.Filter({ type: "bandpass", frequency: 3000, Q: 3 });
  const noiseEnv = new Tone.AmplitudeEnvelope({
    attack: 0,
    decay: 0.01,
    sustain: 0,
    release: 0,
  });
  const noiseVol = new Tone.Volume(-34);
  noise.connect(noiseFilter);
  noiseFilter.connect(noiseEnv);
  noiseEnv.connect(noiseVol);
  noiseVol.toDestination();

  synth.triggerAttackRelease(freq, 0.08);
  noise.start();
  noiseEnv.triggerAttackRelease(0.01);

  setTimeout(() => {
    synth.dispose();
    vol1.dispose();
    noise.stop();
    noise.dispose();
    noiseFilter.dispose();
    noiseEnv.dispose();
    noiseVol.dispose();
  }, 150);
}

// =============================================================================
// GLITCH SOUNDS
// =============================================================================

export function playGlitch(type: "letter" | "dropout"): void {
  if (!canPlayGlitch()) return;

  if (type === "letter") {
    // Square wave, random 800-1200hz, 60ms, -44db
    const freq = 800 + Math.random() * 400;
    const osc = new Tone.Oscillator({ type: "square", frequency: freq });
    const env = new Tone.AmplitudeEnvelope({
      attack: 0,
      decay: 0.06,
      sustain: 0,
      release: 0,
    });
    const vol = new Tone.Volume(-44);

    osc.connect(env);
    env.connect(vol);
    vol.toDestination();

    osc.start();
    env.triggerAttackRelease(0.06);

    setTimeout(() => {
      osc.stop();
      osc.dispose();
      env.dispose();
      vol.dispose();
    }, 100);
  } else {
    // White noise, 80ms, -50db
    const noise = new Tone.Noise({ type: "white" });
    const env = new Tone.AmplitudeEnvelope({
      attack: 0,
      decay: 0.08,
      sustain: 0,
      release: 0,
    });
    const vol = new Tone.Volume(-50);

    noise.connect(env);
    env.connect(vol);
    vol.toDestination();

    noise.start();
    env.triggerAttackRelease(0.08);

    setTimeout(() => {
      noise.stop();
      noise.dispose();
      env.dispose();
      vol.dispose();
    }, 150);
  }
}

// =============================================================================
// MINI NAV TRANSITIONS
// =============================================================================

export function playCollapse(): void {
  if (!canPlayTransition()) return;

  // Sine sweep 280hz -> 180hz, 300ms, -28db
  playSweep(280, 180, 0.3, -28);
}

export function playExpand(): void {
  if (!canPlayTransition()) return;

  // Sine sweep 180hz -> 280hz, 300ms, -28db
  playSweep(180, 280, 0.3, -28);
}

function playSweep(fromFreq: number, toFreq: number, duration: number, volume: number): void {
  const osc = new Tone.Oscillator({ type: "sine", frequency: fromFreq });
  const vol = new Tone.Volume(volume);
  const env = new Tone.AmplitudeEnvelope({
    attack: 0.01,
    decay: duration - 0.02,
    sustain: 0,
    release: 0.01,
  });

  osc.connect(env);
  env.connect(vol);
  vol.toDestination();

  osc.start();
  osc.frequency.rampTo(toFreq, duration);
  env.triggerAttackRelease(duration);

  setTimeout(() => {
    osc.stop();
    osc.dispose();
    env.dispose();
    vol.dispose();
  }, (duration * 1000) + 100);
}

// =============================================================================
// STATE TRANSITIONS (idle/peek/read)
// =============================================================================
// Diegetic audio for zone state changes. Sounds belong to the world.
// - playHoverLong: orb selection with sustained tone (idle -> peek)
// - playDescend: 440hz tone + shimmer through reverb (peek -> read)
// - playAscend: 440hz tone + shimmer with filter sweep (read -> peek/idle)
//
// Both descend/ascend end fully wet (1.00) -- spatial environment never
// fully closes. Filter sweep on ascend does the surfacing work.
// Pitch is fixed 440hz (A5) regardless of active orb -- environment sounds.

/**
 * Extended hover tone for orb selection (idle -> peek).
 * Same pentatonic pitch as playHover but with longer 320ms decay.
 * T2+: full sound. T1/T0: no-op.
 */
export function playHoverLong(orbIndex: number): void {
  if (!canPlayInteraction()) return;

  const freq = HOVER_PITCHES[orbIndex] ?? 440;

  // FM synthesis + opening lowpass sweep — "zooming in" feeling
  const filter = new Tone.Filter({ type: "lowpass", frequency: 800 });
  const vol = new Tone.Volume(-20);
  const synth = new Tone.FMSynth({
    harmonicity: 3,
    modulationIndex: 2,
    oscillator: { type: "sine" },
    modulation: { type: "sine" },
    envelope: { attack: 0.004, decay: 0.35, sustain: 0, release: 0 },
    modulationEnvelope: { attack: 0.004, decay: 0.25, sustain: 0, release: 0 },
  });

  synth.connect(filter);
  filter.connect(vol);
  vol.toDestination();

  // Filter opens as the sound plays — "zooming in"
  filter.frequency.exponentialRampTo(4000, 0.3);

  synth.triggerAttackRelease(freq, 0.35);

  setTimeout(() => {
    synth.dispose();
    filter.dispose();
    vol.dispose();
  }, 500);
}

/**
 * Descend into read mode (peek -> read).
 * Fixed 440hz (A5) tone with shimmer, routed through reverb that opens to full wet.
 * Simultaneously fades ambient from -42db to -80db over 1s (silent — reading mode).
 * T2+: synth + ambient fade. T1: ambient fade only. T0: no-op.
 */
export function playDescend(): void {
  if (gpuTier < 1 || isMuted() || !isInitialized()) return;

  // T1+: Fade ambient to silence (reading mode = no drones, no distraction)
  if (ambientVolume) {
    ambientVolume.volume.rampTo(-80, 1);
  }

  // T2+: Play the descend tone with reverb
  if (gpuTier >= 2) {
    // Reverb: decay 3.5s, wet ramps 0.10 -> 1.00 over 400ms
    const reverb = new Tone.Reverb({ decay: 3.5, wet: 0.1 });

    // LAYER 1: Main tone 440hz, attack 2ms, decay 500ms, -18db
    const synth1 = new Tone.Synth({
      oscillator: { type: "sine" },
      envelope: { attack: 0.002, decay: 0.5, sustain: 0, release: 0 },
    });
    const vol1 = new Tone.Volume(-18);

    // LAYER 2: Shimmer 880hz (1 octave up), attack 2ms, decay 300ms, -30db
    const synth2 = new Tone.Synth({
      oscillator: { type: "sine" },
      envelope: { attack: 0.002, decay: 0.3, sustain: 0, release: 0 },
    });
    const vol2 = new Tone.Volume(-30);

    // Signal chain: both synths -> reverb -> output
    synth1.connect(vol1);
    synth2.connect(vol2);
    vol1.connect(reverb);
    vol2.connect(reverb);
    reverb.toDestination();

    // Ramp reverb wet to full over 400ms
    reverb.wet.rampTo(1.0, 0.4);

    // Trigger both layers
    synth1.triggerAttackRelease(440, 0.5);
    synth2.triggerAttackRelease(880, 0.3);

    // Dispose after reverb tail clears (~2s post trigger)
    setTimeout(() => {
      synth1.dispose();
      synth2.dispose();
      vol1.dispose();
      vol2.dispose();
      reverb.dispose();
    }, 2000);
  }
}

/**
 * Blog deep-scroll enter — user has scrolled past the orb spacer into the card grid.
 * Derived from playDescend() but lighter: A5 (blog's pitch), shorter decay (300ms),
 * softer fixed reverb (wet 0.3), no ambient duck. Plays once on state flip only.
 * T2+: synth + reverb. T0/T1: no-op.
 */
export function playBlogEnterRead(): void {
  if (gpuTier < 2 || isMuted() || !isInitialized()) return;

  // Soft reverb — decay 2s, fixed wet 0.3 (no cinematic ramp)
  const reverb = new Tone.Reverb({ decay: 2, wet: 0.3 });

  // Blog pitch: A5 (440Hz = HOVER_PITCHES[2]), shorter/quieter than main descend
  const synth = new Tone.Synth({
    oscillator: { type: "sine" },
    envelope: { attack: 0.002, decay: 0.3, sustain: 0, release: 0 },
  });
  const vol = new Tone.Volume(-24);

  synth.connect(vol);
  vol.connect(reverb);
  reverb.toDestination();

  synth.triggerAttackRelease(440, 0.3);

  // Dispose after reverb tail clears
  setTimeout(() => {
    synth.dispose();
    vol.dispose();
    reverb.dispose();
  }, 1500);
}

/**
 * Ascend back to ring (read -> peek or read -> idle).
 * Fixed 440hz (A5) tone with shimmer, filter sweep opens as we surface.
 * Reverb opens to full wet. NO settle chime - visual carries the moment.
 * Simultaneously fades ambient from -48db to -42db over 1s.
 * T2+: synth + ambient fade. T1: ambient fade only. T0: no-op.
 */
export function playAscend(): void {
  if (gpuTier < 1 || isMuted() || !isInitialized()) return;

  // T1+: Fade ambient back up (returning to ring) — -52 is new peak
  if (ambientVolume) {
    ambientVolume.volume.rampTo(-52, 1);
  }

  // T2+: Play the ascend tone with filter sweep + reverb
  if (gpuTier >= 2) {
    // Reverb: decay 2.5s, wet ramps 0.26 -> 1.00 over 400ms
    const reverb = new Tone.Reverb({ decay: 2.5, wet: 0.26 });

    // Filter: lowpass, exponential ramp 400hz -> 5200hz over 400ms
    const filter = new Tone.Filter({ type: "lowpass", frequency: 400 });

    // LAYER 1: Main tone 440hz, attack 2ms, decay 350ms, -18db
    const synth1 = new Tone.Synth({
      oscillator: { type: "sine" },
      envelope: { attack: 0.002, decay: 0.35, sustain: 0, release: 0 },
    });
    const vol1 = new Tone.Volume(-18);

    // LAYER 2: Shimmer 1760hz (2 octaves up), attack 2ms, decay 200ms, -28db
    // Routes direct to reverb (no filter)
    const synth2 = new Tone.Synth({
      oscillator: { type: "sine" },
      envelope: { attack: 0.002, decay: 0.2, sustain: 0, release: 0 },
    });
    const vol2 = new Tone.Volume(-28);

    // Signal chain: synth1 -> filter -> reverb -> output
    // Shimmer: synth2 -> reverb (direct, no filter)
    synth1.connect(vol1);
    vol1.connect(filter);
    filter.connect(reverb);

    synth2.connect(vol2);
    vol2.connect(reverb);

    reverb.toDestination();

    // Ramp filter frequency 400hz -> 5200hz (exponential) over 400ms
    filter.frequency.exponentialRampTo(5200, 0.4);

    // Ramp reverb wet to full over 400ms
    reverb.wet.rampTo(1.0, 0.4);

    // Trigger both layers
    synth1.triggerAttackRelease(440, 0.35);
    synth2.triggerAttackRelease(1760, 0.2);

    // Dispose after reverb tail clears (~2s post trigger)
    setTimeout(() => {
      synth1.dispose();
      synth2.dispose();
      vol1.dispose();
      vol2.dispose();
      filter.dispose();
      reverb.dispose();
    }, 2000);
  }
}

// =============================================================================
// AMBIENT ENVIRONMENTAL BLIPS
// =============================================================================
// Periodic synthesized environmental sounds layered over the ambient drones.
// Satellite pings, radar sweeps, data blips — subliminal space station texture.
// Gated to T2+ (T1 gets drones only). Paused during reading mode.

let blipsActive = false;
let blipTimeouts: ReturnType<typeof setTimeout>[] = [];

function scheduleBlip(type: "satellite" | "radar" | "data"): void {
  if (!blipsActive) return;

  const ranges = {
    satellite: { min: 8000, max: 15000 },
    radar: { min: 20000, max: 35000 },
    data: { min: 12000, max: 20000 },
  };

  const range = ranges[type];
  const delay = range.min + Math.random() * (range.max - range.min);

  const timeout = setTimeout(() => {
    // Check mute state (reads from localStorage as source of truth)
    const mutedNow = isMuted();
    if (!blipsActive || mutedNow || !isInitialized()) {
      if (blipsActive && !mutedNow) scheduleBlip(type); // Only reschedule if still active and not muted
      return;
    }

    switch (type) {
      case "satellite":
        playSatellitePing();
        break;
      case "radar":
        playRadarSweep();
        break;
      case "data":
        playDataBlip();
        break;
    }

    scheduleBlip(type); // Schedule next occurrence
  }, delay);

  blipTimeouts.push(timeout);
}

function playSatellitePing(): void {
  if (isMuted()) return;
  // Focused sine ping — narrow bandpass, heavy reverb, distant
  const freq = 1200 + (Math.random() * 100 - 50); // ±50Hz drift
  const filter = new Tone.Filter({ type: "bandpass", frequency: freq, Q: 8 });
  const reverb = new Tone.Reverb({ decay: 4, wet: 0.7 });
  const vol = new Tone.Volume(-44);

  const synth = new Tone.Synth({
    oscillator: { type: "sine" },
    envelope: { attack: 0.005, decay: 0.12, sustain: 0, release: 0 },
  });

  synth.connect(filter);
  filter.connect(reverb);
  reverb.connect(vol);
  vol.toDestination();

  synth.triggerAttackRelease(freq, 0.12);

  setTimeout(() => {
    synth.dispose();
    filter.dispose();
    reverb.dispose();
    vol.dispose();
  }, 4500);
}

function playRadarSweep(): void {
  if (isMuted()) return;
  // Frequency sweep with autofilter for movement — very distant
  const osc = new Tone.Oscillator({ type: "sine", frequency: 400 });
  const autoFilter = new Tone.AutoFilter({ frequency: 0.3, wet: 0.4 }).start();
  const reverb = new Tone.Reverb({ decay: 5, wet: 0.8 });
  const vol = new Tone.Volume(-46);
  const env = new Tone.AmplitudeEnvelope({
    attack: 0.05,
    decay: 0.55,
    sustain: 0,
    release: 0,
  });

  osc.connect(env);
  env.connect(autoFilter);
  autoFilter.connect(reverb);
  reverb.connect(vol);
  vol.toDestination();

  osc.start();
  osc.frequency.exponentialRampTo(1800, 0.6);
  env.triggerAttackRelease(0.6);

  setTimeout(() => {
    osc.stop();
    osc.dispose();
    env.dispose();
    autoFilter.dispose();
    reverb.dispose();
    vol.dispose();
  }, 5500);
}

function playDataBlip(): void {
  if (isMuted()) return;
  // 3-note ascending triplet — quick telemetry burst
  const baseFreq = 800;
  const notes = [baseFreq, baseFreq * 1.25, baseFreq * 1.5]; // 800, 1000, 1200
  const filter = new Tone.Filter({ type: "highpass", frequency: 600 });
  const reverb = new Tone.Reverb({ decay: 2, wet: 0.5 });
  const vol = new Tone.Volume(-42);

  filter.connect(reverb);
  reverb.connect(vol);
  vol.toDestination();

  notes.forEach((freq, i) => {
    const synth = new Tone.Synth({
      oscillator: { type: "sine" },
      envelope: { attack: 0.002, decay: 0.03, sustain: 0, release: 0 },
    });
    synth.connect(filter);

    // Stagger: 30ms per note + 10ms gap = 40ms per step
    setTimeout(() => {
      synth.triggerAttackRelease(freq, 0.03);
      setTimeout(() => synth.dispose(), 100);
    }, i * 40);
  });

  setTimeout(() => {
    filter.dispose();
    reverb.dispose();
    vol.dispose();
  }, 2500);
}

export function startAmbientBlips(): void {
  if (blipsActive) return;
  if (gpuTier < 2 || isMuted() || !isInitialized()) return;

  blipsActive = true;
  scheduleBlip("satellite");
  scheduleBlip("radar");
  scheduleBlip("data");
  console.log("[SpatialAudio] Ambient blips started");
}

export function stopAmbientBlips(): void {
  blipsActive = false;
  blipTimeouts.forEach(clearTimeout);
  blipTimeouts = [];
  console.log("[SpatialAudio] Ambient blips stopped");
}

/**
 * Pause blips during reading mode (drones continue, blips stop).
 * Resume when returning to ring.
 */
export function setBlipsReadingMode(reading: boolean): void {
  if (reading) {
    stopAmbientBlips();
  } else if (gpuTier >= 2 && !isMuted() && isInitialized()) {
    startAmbientBlips();
  }
}

// =============================================================================
// CLEANUP
// =============================================================================

export function dispose(): void {
  stopAmbient();
  stopAmbientBlips();
  stopEmergence();
  stopBootShrink();

  ambientOsc1?.dispose();
  ambientOsc2?.dispose();
  ambientReverb?.dispose();
  ambientFilter?.dispose();
  ambientVolume?.dispose();

  rotationOsc?.dispose();
  rotationFilter?.dispose();
  rotationVolume?.dispose();

  console.log("[SpatialAudio] Disposed");
}
